import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';

import productsApi from '../../../api/productsApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';
import { categoryIndentSx, flattenCategoryTree, getDescendantIds } from '../../../utils/categoryTree';

const schema = yup.object({
  name: yup.string().trim().required('El nombre es obligatorio').max(255),
  slug: yup.string().nullable(),
  parent_id: yup.mixed().nullable(),
});

const emptyValues = {
  name: '',
  slug: '',
  parent_id: '',
};

// Formulario de alta/edicion de una categoria. `category` nulo significa "crear nueva".
// El slug es opcional: si se deja vacio, el backend lo genera a partir del nombre.
export default function CategoryFormDialog({ open, onClose, onSaved, category, defaultParentId, categories }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(category);

  // Una categoria no puede tener como padre a si misma ni a una de sus subcategorias (crearia un ciclo).
  const parentOptions = useMemo(() => {
    const excluded = category ? getDescendantIds(categories, category.id).add(category.id) : new Set();
    return flattenCategoryTree(categories).filter((option) => !excluded.has(option.id));
  }, [categories, category]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      category
        ? {
            name: category.name,
            slug: category.slug || '',
            parent_id: category.parent_id || '',
          }
        : { ...emptyValues, parent_id: defaultParentId || '' }
    );
  }, [open, category, defaultParentId, reset]);

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      slug: data.slug || undefined,
      parent_id: data.parent_id || null,
    };

    try {
      if (isEditing) {
        await productsApi.updateCategory(category.id, payload);
        enqueueSnackbar('Categoría actualizada correctamente.', { variant: 'success' });
      } else {
        await productsApi.createCategory(payload);
        enqueueSnackbar('Categoría creada correctamente.', { variant: 'success' });
      }
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar la categoría.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar categoría' : defaultParentId ? 'Nueva subcategoría' : 'Nueva categoría'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="category-form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root?.message && <Alert severity="error">{errors.root.message}</Alert>}

          <TextField
            label="Nombre"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register('name')}
          />

          <TextField
            label="Slug (opcional)"
            fullWidth
            placeholder="Se genera automáticamente si lo dejas vacío"
            error={Boolean(errors.slug)}
            helperText={errors.slug?.message}
            {...register('slug')}
          />

          <Controller
            name="parent_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Categoría padre (opcional)"
                fullWidth
                error={Boolean(errors.parent_id)}
                helperText={errors.parent_id?.message}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (value) =>
                      value
                        ? parentOptions.find((option) => option.id === value)?.path || ''
                        : 'Ninguna (categoría principal)',
                  },
                  inputLabel: { shrink: true },
                }}
              >
                <MenuItem value="">Ninguna (categoría principal)</MenuItem>
                {parentOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id} sx={categoryIndentSx(option.depth)}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="category-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CategoryFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  category: PropTypes.object,
  defaultParentId: PropTypes.number,
  categories: PropTypes.array.isRequired,
};

CategoryFormDialog.defaultProps = {
  category: null,
  defaultParentId: null,
};
